import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  Alert, Badge, Button, ButtonGroup, Form, Input, ListGroupItem,
  Media, Modal, ModalBody, ModalFooter, ModalHeader,
} from 'reactstrap';
import Voice from './Voice';
import Loading from '../../Loading';
import Certificates from '../../../_Certificates';
import { sendMessage, uploadMessageImage } from '../../../../utils/Api';
import { toLocalTime } from '../../../../utils/helper';

const getExtension = filename => filename.split('.').pop();
const getType = ext => [
  // TODO: get types from server
  { id: 0, name: 'Document', value: 'document', extensions: '.txt, .doc, .docx, .xls, .xlsx, .pdf, .ppt, .pptx' },
  { id: 1, name: 'Picture', value: 'image', extensions: '.png, .jpeg, .jpg, .gif' },
  { id: 2, name: 'Video', value: 'video', extensions: '.avi, .mp4' },
  { id: 3, name: 'Audio', value: 'audio', extensions: '.mp3, .aac, .m4a' },
  { id: 4, name: 'Link', value: 'link', extensions: '' },
  { id: 5, name: 'Text', value: 'text', extensions: '' },
].filter(({ extensions }) => extensions.split(', ').includes(`.${ext}`))[0].value;

class StartMessage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoaded: true,
      text: '',
      user: {},
      modal: false,
      errorMessage: '',
    };

    this.filesSizeLimit = 5242880; // 5 MB

    this.toggle = this.toggle.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleAddMessage = this.handleAddMessage.bind(this);
    this.handleUploadImage = this.handleUploadImage.bind(this);
    this.addCertificate = this.addCertificate.bind(this);
    this.resetError = this.resetError.bind(this);
  }

  toggle() {
    const { modal } = this.state;
    this.setState({ modal: !modal });
  }

  handleChange({ target }) {
    this.setState({ [target.id]: target.value });
  }

  addCertificate(certificateId, fileLink, originalName) {
    this.setState({
      isLoaded: true,
      modal: false,
      storage_id: certificateId,
      file_link: fileLink,
      original_name: originalName,
    });
  }

  handleAddMessage() {
    const { text, storage_id } = this.state;
    const { chatId, onAddComplete } = this.props;

    if (!text && !storage_id) {
      return;
    }

    this.setState({ isLoaded: false });
    sendMessage(chatId, {
      ...(text ? { text } : {}),
      ...(storage_id ? { storage_id } : {}),
    }).then(({ error }) => {
      if (!error) {
        this.setState({
          isLoaded: true,
          storage_id: null,
          text: '',
        });
      } else {
        const message = error.code ? `error_code_${error.code}` : error.msg;
        this.setState({
          isLoaded: true,
          errorMessage: message || 'error adding message',
        });
      }
      onAddComplete();
    });
  }

  handleUploadImage({ target }) {
    if (target.files[0].size > this.filesSizeLimit) {
      this.setState({
        errorMessage: this.props.intl.formatMessage({ id: 'error_filesizeExceeded' }),
      });
      return;
    }
    this.setState({
      isLoaded: false,
      errorMessage: null,
    });
    uploadMessageImage(target.files[0]).then(({ id, original_name, link, error }) => {
      if (!error) {
        this.setState({
          storage_id: id,
          original_name,
          file_link: link,
          isLoaded: true,
          errorMessage: '',
        });
      } else {
        const message = error.code ? `error_code_${error.code}` : error.msg;
        this.setState({
          isLoaded: true,
          errorMessage: message || 'error uploading image',
        });
      }
    });
  }

  resetError() {
    this.setState({ errorMessage: '' });
  }

  render() {
    const { user, status, isLoaded, modal, errorMessage } = this.state;
    const { intl, chatId, onAddComplete } = this.props;

    return (
      <React.Fragment>
        {!isLoaded && <Loading />}
        <Modal isOpen={modal} toggle={this.toggle} className="modal-lg">
          <ModalHeader toggle={this.toggle}><FormattedMessage id="messages_attachCertificate" /></ModalHeader>
          <ModalBody>
            <Certificates onSelect={this.addCertificate} />
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={this.handleAddCertificate}><FormattedMessage id="overall_input_attach" /></Button>
            <Button color="secondary" onClick={this.toggle}><FormattedMessage id="overall_input_cancel" /></Button>
          </ModalFooter>
        </Modal>
        <Form>
          <ListGroupItem className="send_message_form">
            <Media tag="li">
              <Media left>{user.user_avatar && <img src={`${user.user_avatar}?h=64&w=64`} alt="" />}</Media>
              <Media body>
                <Media heading>
                  {status ? <Badge pill /> : ''}
                  {`${user.salutation || ''} ${user.first_name || ''} ${user.last_name || ''}`}
                </Media>
                {errorMessage && <Alert color="danger"><FormattedMessage id={errorMessage} /></Alert>}
                <div>
                  <Input
                    id="text"
                    placeholder={intl.formatMessage({ id: 'messages_messageText' })}
                    value={this.state.text || ''}
                    onChange={this.handleChange}
                  />
                </div>
                <div>
                  <Input
                    id="attachment"
                    type="file"
                    onChange={this.handleUploadImage}
                    style={{
                      position: 'absolute',
                      left: -2000,
                      opacity: 0,
                      pointerEvents: 'none',
                      width: '1px',
                      height: '1px',
                      visibility: 'hidden',
                    }}
                  />
                  {this.state.storage_id &&
                  ((getType(getExtension(this.state.file_link)) === 'image' || getType(getExtension(this.state.file_link)) === 'video') ?
                    <img
                      src={`${this.state.file_link}?h=128&w=128`}
                      alt={this.state.original_name || ''}
                      style={{ maxWidth: 128, maxHeight: 128 }}
                    />
                    :
                    <a href={this.state.file_link}>{this.state.original_name || ''}</a>
                  )}
                </div>
                {/* <div>{toLocalTime(this.state.datetime)}</div> */}
                <ButtonGroup style={{ display: 'flex', flexWrap: 'wrap' }}>
                  <Button
                    color="secondary"
                    className="mrg-r-md mrg-t-md"
                    onClick={() => document.getElementById('attachment').click()}
                  ><FormattedMessage id="messages_attachFile" /></Button>
                  <Button
                    color="secondary"
                    className="mrg-r-md mrg-t-md"
                    onClick={this.toggle}
                  ><FormattedMessage id="messages_attachCertificate" /></Button>
                  <Voice chatId={chatId} onAddComplete={onAddComplete} />
                  <Button
                  color="secondary"
                  type="submit"
                  onClick={() => {
                    this.handleAddMessage();
                    this.resetError();
                  }}
                  className="text-hide mrg-t-md button_send_message"
                >
                  <i className="icon_c icon_c_bg icon_send_message b-0" />
                  {/* <FormattedMessage id="overall_input_send" /> */}
                </Button>
                </ButtonGroup>
              </Media>
            </Media>
          </ListGroupItem>
        </Form>
      </React.Fragment>
    );
  }
}

StartMessage.propTypes = {
  intl: PropTypes.objectOf(PropTypes.any),
  chatId: PropTypes.number,
  onAddComplete: PropTypes.func.isRequired,
};

export default injectIntl(StartMessage);



// WEBPACK FOOTER //
// ./src/views/_Chats/Messages/New/index.js